import { useEffect, useRef, useState } from 'react';
import { useToast } from '../../../shared/hooks/use-toast';
import { ApiError } from '../../../shared/shared.api';
import { dispatchTrip, getDispatchPreview, type DispatchAllocations, type DispatchPreview } from '../dispatch.api';

export type DispatchTripState =
  { status: 'loading' } | { status: 'loaded'; preview: DispatchPreview } | { status: 'forbidden' } | { status: 'not-found' } | { status: 'error' };

function toErrorState(err: unknown): DispatchTripState {
  if (err instanceof ApiError && err.status === 403) return { status: 'forbidden' };
  if (err instanceof ApiError && err.status === 404) return { status: 'not-found' };
  return { status: 'error' };
}

/** The server's own words for a refusal (409 not ready, not eligible, taken meanwhile); anything else is a generic failure. */
function refusalMessage(err: unknown): string {
  const body = err instanceof ApiError ? err.body : null;
  const message = typeof body === 'object' && body !== null && 'message' in body ? (body as { message: unknown }).message : null;
  return typeof message === 'string' ? message : 'Could not dispatch the trip. Try again.';
}

/**
 * The pre-flight read, and the dispatch itself. One idempotency key per page visit, so a double click or a retry after a dropped
 * connection returns the first dispatch rather than a second attempt.
 */
export function useDispatchTrip(tripId: string) {
  const [state, setState] = useState<DispatchTripState>({ status: 'loading' });
  const [readCount, setReadCount] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const idempotencyKey = useRef(crypto.randomUUID());
  const toast = useToast();

  useEffect(() => {
    let cancelled = false;
    getDispatchPreview(tripId)
      .then((preview) => {
        if (!cancelled) setState({ status: 'loaded', preview });
      })
      .catch((err) => {
        if (!cancelled) setState(toErrorState(err));
      });
    return () => {
      cancelled = true;
    };
  }, [tripId, readCount]);

  const reload = () => setReadCount((count) => count + 1);

  async function dispatch(allocations: DispatchAllocations): Promise<void> {
    setIsSubmitting(true);
    setError(null);
    try {
      const { trip } = await dispatchTrip(tripId, idempotencyKey.current, allocations);
      toast.success(`Trip ${trip.number} dispatched`);
    } catch (err) {
      setError(refusalMessage(err));
    } finally {
      setIsSubmitting(false);
      reload();
    }
  }

  return { state, reload, dispatch, isSubmitting, error };
}
